import React, { useEffect, useState } from 'react';
import { Box, Heading, Text, Link, Spinner, UnorderedList, ListItem } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { getAvisoPrivacidad } from '../../api/legalPublico';
import { logError } from '../../utils/devLog';

// [NGT 237 IX.B.3.c] El aviso de privacidad que la norma manda publicar EN los
// sistemas. El texto no vive acá: viene del backend (aviso_privacidad_service.py),
// versionado, por el mismo motivo que los textos de consentimiento — si la
// pantalla tuviera su propia copia, acabaría diciendo algo distinto de lo que el
// sistema declara.
//
// Se pinta en dos lugares con el mismo componente: dentro de "Mi cita", desplegado
// ahí mismo (`compacto`), y en la página /privacidad. En "Mi cita" NO puede ser un
// enlace que navegue: el token del enlace viaja en el fragmento de la URL
// (`/mi-cita#<token>`, ver M-4) y salir de la pantalla lo perdería.
const AvisoPrivacidad = ({ compacto = false }) => {
    const [aviso, setAviso] = useState(null);
    const [fallo, setFalló] = useState(false);

    useEffect(() => {
        let cancelado = false;
        getAvisoPrivacidad()
            .then((res) => { if (!cancelado) setAviso(res.data.data); })
            .catch((e) => { logError(e); if (!cancelado) setFalló(true); });
        return () => { cancelado = true; };
    }, []);

    const tam = compacto ? '12px' : '14px';

    if (fallo) {
        return (
            <Text fontSize={tam} color="gray.600">
                No se pudo cargar el aviso en este momento. Puedes pedirlo en el centro donde te
                atiendes.
            </Text>
        );
    }

    if (!aviso) return <Spinner size="sm" color="#553285" />;

    const contactos = aviso.contactos || {};

    return (
        <Box textAlign="left">
            {!compacto && (
                <Heading as="h1" fontSize="22px" color="#553285" mb="4px">
                    {aviso.titulo}
                </Heading>
            )}

            {aviso.prestador && (
                <Text fontSize={tam} color="gray.700" mb="12px">
                    Prestador responsable: <strong>{aviso.prestador}</strong>
                </Text>
            )}

            {aviso.secciones.map((seccion) => (
                <Box key={seccion.titulo} mb="14px">
                    <Text fontSize={compacto ? '13px' : '15px'} fontWeight="600" color="gray.700" mb="4px">
                        {seccion.titulo}
                    </Text>
                    {seccion.parrafos.map((parrafo) => (
                        <Text key={parrafo.slice(0, 40)} fontSize={tam} color="gray.600" mb="6px">
                            {parrafo}
                        </Text>
                    ))}
                    {seccion.vinetas.length > 0 && (
                        <UnorderedList fontSize={tam} color="gray.600" spacing="4px" pl="18px">
                            {seccion.vinetas.map((vineta) => (
                                <ListItem key={vineta.slice(0, 40)}>{vineta}</ListItem>
                            ))}
                        </UnorderedList>
                    )}
                    {seccion.cierre.map((parrafo) => (
                        <Text key={parrafo.slice(0, 40)} fontSize={tam} color="gray.600" mt="6px">
                            {parrafo}
                        </Text>
                    ))}
                </Box>
            ))}

            {/* Las dos casillas vienen de la misma fuente que las de "Mi cita"
                (variables de entorno del despliegue) y no de este texto, así que
                una dirección cambiada no queda desactualizada en dos lugares.
                Cualquiera puede venir en null: entonces no se dibuja, porque
                publicar un canal al que nadie contesta es peor que no publicarlo. */}
            {(contactos.derechos || contactos.incidentes) && (
                <Box borderTop="1px solid #edf2f7" pt="10px">
                    <Text fontSize={compacto ? '13px' : '15px'} fontWeight="600" color="gray.700" mb="4px">
                        Dónde escribir
                    </Text>
                    {contactos.derechos && (
                        <Text fontSize={tam} color="gray.600" mb="4px">
                            Para ejercer tus derechos sobre tu información:{' '}
                            <Link href={`mailto:${contactos.derechos}`} color="#553285" textDecoration="underline">
                                {contactos.derechos}
                            </Link>
                        </Text>
                    )}
                    {contactos.incidentes && (
                        <Text fontSize={tam} color="gray.600">
                            Para comunicar un incidente de seguridad:{' '}
                            <Link href={`mailto:${contactos.incidentes}`} color="#553285" textDecoration="underline">
                                {contactos.incidentes}
                            </Link>
                        </Text>
                    )}
                </Box>
            )}
        </Box>
    );
};

AvisoPrivacidad.propTypes = {
    compacto: PropTypes.bool,
};

export default AvisoPrivacidad;
